import React, { useCallback, useEffect, useRef, useState } from 'react';
import FeedbackButton from '../../components/FeedbackButton';

const VideoPlayer = ({
  videoId,
  disableInteractiveFeatures = false,
  onProgress,
  onTiming,
  onReady,
  showFeedbackButton = false,
  feedbackContentIds = [],
}) => {
  const playerRef = useRef(null);
  const intervalRef = useRef(null);
  const [apiReady, setApiReady] = useState(!!(window.YT && window.YT.Player));

  const reportProgress = useCallback((currentTime, duration) => {
    if (onTiming) {
      onTiming({ currentTime, duration });
    }

    if (onProgress && duration > 0) {
      onProgress({
        currentTime,
        duration,
        videoProgress: (currentTime / duration) * 100,
      });
    }
  }, [onProgress, onTiming]);

  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      window.onYouTubeIframeAPIReady = () => {
        setApiReady(true);
      };
    } else {
      setApiReady(true);
    }
  }, []);

  const onPlayerStateChange = useCallback((event) => {
    if (event.data === window.YT.PlayerState.PLAYING) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        if (playerRef.current && playerRef.current.getCurrentTime) {
          const currentTime = playerRef.current.getCurrentTime();
          const duration = playerRef.current.getDuration();
          reportProgress(currentTime, duration);
        }
      }, 500);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (playerRef.current?.getCurrentTime && playerRef.current?.getDuration) {
        const updatedCurrentTime = playerRef.current.getCurrentTime();
        const updatedDuration = playerRef.current.getDuration();
        reportProgress(updatedCurrentTime, updatedDuration);
      }
    }
  }, [reportProgress]);

  useEffect(() => {
    if (apiReady && videoId) {
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (error) {
          console.error('Error destroying player:', error);
        }
      }

      playerRef.current = new window.YT.Player('video-player', {
        height: '100%',
        width: '100%',
        videoId: videoId,
        playerVars: {
          autoplay: 1,
          rel: 0,
        },
        events: {
          onStateChange: onPlayerStateChange,
          onReady: (event) => {
            const newDuration = event.target.getDuration();
            const newCurrentTime = event.target.getCurrentTime();
            reportProgress(newCurrentTime, newDuration);
            if (onReady) {
              onReady(event.target);
            }
          },
        },
      });
    }

    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (error) {
          // ignore
        }
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [apiReady, onPlayerStateChange, onReady, reportProgress, videoId]);

  return (
    <div className="p-2 relative">
      <div>
        <div className="w-full aspect-video rounded-xl overflow-hidden bg-black">
          <div id="video-player"></div>
        </div>
      </div>
      {!disableInteractiveFeatures && showFeedbackButton && (
        <div className="absolute right-0 flex items-center m-2 top-0">
          <FeedbackButton contentIds={feedbackContentIds} />
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
